import React, { useRef, useEffect, useState, useCallback, MouseEvent } from 'react'
import parse from 'html-react-parser'
import { useHistory, useLocation } from "react-router-dom"
import { useMediaQuery } from 'react-responsive'

import Item, { ItemLevel1, ItemLevel2 } from './types/Item'
import Global from './types/Global'
import Level2LayoutProps from './types/Level2LayoutProps'
import Level3LayoutProps from './types/Level3LayoutProps'
import Event from '../events/types/Event'
import Page from './types/Page'
import SearchResults from './types/SearchResults'

import { Search } from './level2/Search'
import Language from './level2/Language'
import ActivitiesEvents from './level2/ActivitiesEvents'
import Level2Default from './level2/Default'

import Level3Default from './level3/Default'
import Library from './level3/Library'
import Results from './level3/Results'

const secondaryIcons: {
  [key: string]: string,
} = {
  'shortcuts': require("~images/icon/noun_Card_black_3676875.svg") as string,
  'search': require("~images/header/search.svg") as string,
  'language': require("~images/header/languages.svg") as string,
}

const Level2Layouts : {
  [key: string]: React.FunctionComponent<Level2LayoutProps>
} = {
  'language': Language,
  'search': Search,
  'activities-events': ActivitiesEvents,
  'default': Level2Default,
}

const Level3Layouts : {
  [key: string]: React.FunctionComponent<Level3LayoutProps>
} = {
  'default': Level3Default,
  'search/results': Results,
  'people-help/locations-hours': Library
}

const hiddenLevel2 = [
  'search/results'
]

const level1slugs = [
  'people-help',
  'things-to-borrow',
  'things-to-use',
  'activities-events',
  'shortcuts',
  'search',
  'language'
]

const INITIAL_PAGE = window.location.pathname
const IS_GOOGLE_TRANSLATE = !!window.location.host.match('bpl-bc-ca.translate.goog')

export default ({ globals, menus, events } : { globals: Array<Global>, menus: Array<Item>, events: Array<Event> }) => {
  let history = useHistory()
  let location = useLocation()
  let barsRef = useRef<HTMLInputElement>(null)


  const isTablet = useMediaQuery({ query: '(min-width: 600px)' })

  // if (IS_GOOGLE_TRANSLATE) {
  //   var urlParams;
  //   var match,
  //       pl     = /\+/g,  // Regex for replacing addition symbol with a space
  //       search = /([^&=]+)=?([^&]*)/g,
  //       decode = function (s) { return decodeURIComponent(s.replace(pl, " ")); },
  //       query  = window.location.search.substring(1);

  //   urlParams = {};
  //   while (match = search.exec(query)) {
  //     urlParams[decode(match[1])] = decode(match[2]);
  //   }
  //   const initialPath = urlParams.u.split('/').slice(3).join('/')
  // }
  // else {
  // }
  const initialPath = window.location.pathname

  const [menuState, setMenuState] = useState<string>(initialPath);
  const path = menuState.split('/').filter(part => part !== '')
  const alert: Global | undefined = globals.find((o) => o.handle === 'alert')

  const level1: Array<Item> = menus.filter(o => o.type === 'menuLevel1').map((o) => {
    return {
      ...o,
      active: o.slug === path[0] && (path.length === 1 || path.length === 2),
    }
  })
  const primary: Array<Item> = level1.slice(0, level1.length - 3)
  const secondary = level1.slice(-3).map((item) => {
    return {
      ...item,
      icon: secondaryIcons[item.slug]
    }
  })

  const level1active = level1.find(o => o.type === 'menuLevel1' && o.active) as ItemLevel1
  const level2: Array<ItemLevel2> = menus.filter(o => (o.type === 'menuLevel2' || o.type === 'locationsAndHours') && level1active && level1active.id === o.parent).map((o) => {
    return {
      ...o,
      active: o.slug === path[1] && path.length === 2,
      slug: level1active ? level1active.slug + '/' + o.slug : o.slug,
    } as ItemLevel2
  })
  const level2active = level1active && level1active.slug === 'activities-events' ? level2.find(o => o.parent === level1active.id) : level2.find(o => o.active) as ItemLevel2

  if (level1active) {
    document.body.classList.add('nav-locked')
  }
  else {
    document.body.classList.remove('nav-locked')
  }

  const goto = (e: React.MouseEvent, item: Item) => {
    let url = INITIAL_PAGE

    if (item.slug === 'shortcuts') {
      window.location.href = 'https://burnaby.bibliocommons.com/user/login?destination=%2Fuser_dashboard';
      return;
    }
    
    if (item.active) {
      const parent = menus.find(o => o.id === item.parent)
      url = parent === undefined ? INITIAL_PAGE : '/' + parent.slug
    }
    else {
      url = '/' + item.slug
    }

    e.preventDefault()
    setMenuState(url)
    if (!IS_GOOGLE_TRANSLATE) {
      history.replace(url)
    }

    if (!item.active) {
      // Scroll on next tick.
      const target = e.target as HTMLInputElement
      const parent = target.offsetParent as HTMLInputElement
      const toScroll = parent.offsetTop

      if (!isTablet) {
        setTimeout(() => {
          if (barsRef && barsRef.current && typeof barsRef.current.scrollTo === 'function') {
            barsRef.current.scrollTo(0, toScroll)
          }
        }, 100)
      }
    }
  }

  const closeMenu = (event?: MouseEvent<HTMLDivElement>) : void => {
    const activeMenus = INITIAL_PAGE.split('/')
    activeMenus.shift()

    const nextUrl = level1slugs.includes(activeMenus[0]) ? '/' : INITIAL_PAGE
    
    setMenuState(nextUrl)

    if (!IS_GOOGLE_TRANSLATE) {
      history.replace(nextUrl)
    }
  }

  const handleSearch = (term: string) => {
    setMenuState('/search/results')
    history.replace(`/search/results?${term}`)
  }

  const MenuItem = ({ item }: { item: Item }) => {
    return <li className={'menu-item-' + item.slug + (item.active || item.slug === path[0] ? " active" : '')}>
      <a onClick={(e) => { goto(e, item) }} className="primary-link" href={item.slug === 'shortcuts' ? 'https://burnaby.bibliocommons.com/user/login?destination=%2Fuser_dashboard' : item.url} aria-expanded={(item.active || item.slug === path[0]) ? "true" : "false"} aria-controls={item.slug} aria-label={item.slug === "language" ? item.title : null}>
        {item.icon && <img width="20" height="20" src={item.icon} alt="" />}
        <span>{item.title}</span>
      </a>
    </li>
  }

  // Not 100% sure we need useCallback, found here though: https://stackoverflow.com/a/55566585
  const handleUserKeyPress = useCallback(event => {

    let navFocusableItems = [] as HTMLElement[];
    if(event.target.classList.contains('primary-link')) {
      navFocusableItems = Array.from(document.querySelectorAll('.navigation a'));
    }else {
      const nav = event.target.closest('div[class*="level-"]');
      if(!nav) return;
      navFocusableItems = Array.from(nav.querySelectorAll('a, button, input'));
    }

    if(event.target.tagName === 'INPUT') return;
  
    if (event.key === 'Escape') {
      closeMenu()
    }

    if (event.key === 'ArrowRight') {
      if (navFocusableItems.indexOf(event.target) === navFocusableItems.length - 1){
        const firstElement = navFocusableItems[0] as HTMLAnchorElement;
        firstElement.focus();
        return;
      };

      
      if (event.target.getAttribute('aria-expanded') === 'true') {
        const subNav = document.getElementById(event.target.getAttribute('aria-controls')) as HTMLDivElement;
        const subNavFocusableItems =  subNav ? Array.from(subNav.querySelectorAll('a, button, input')) as HTMLElement[] : [];
        subNavFocusableItems[0].focus();
        
      } else {
        if (navFocusableItems.indexOf(event.target) > -1) {
          const currentIndex = navFocusableItems.indexOf(event.target);
          const nextElement = navFocusableItems[currentIndex + 1] as HTMLAnchorElement;
          nextElement.focus();
        }
      }
    }

    // if left arrow go to previous menu item
    if (event.key === 'ArrowLeft' ) {
      if (navFocusableItems.indexOf(event.target) === 0){
        const lastElement = navFocusableItems[navFocusableItems.length - 1] as HTMLAnchorElement;
        lastElement.focus();
        return;
      };
      if (navFocusableItems.indexOf(event.target) > -1) {
        const currentIndex = navFocusableItems.indexOf(event.target);
        const prevElement = navFocusableItems[currentIndex - 1] as HTMLAnchorElement;
        prevElement.focus();
      }
    }

    // if space or enter, open menu item and focus on first focusable item in subnav
    if (event.key === ' ' || event.key === 'Enter' || event.key == "ArrowDown" && event.target.hasAttribute('aria-expanded')) {
      event.preventDefault();
      event.target.click();
      const subNav = document.getElementById(event.target.getAttribute('aria-controls')) as HTMLDivElement;
      const subNavFocusableItems =  subNav ? Array.from(subNav.querySelectorAll('a, button, input')) as HTMLElement[] : [];
      subNavFocusableItems[0].focus();
    }
    // if up arrow, close subnav and focus on parent menu item
    if (event.key === 'ArrowUp') {
      const currentLevelMenu = event.target.closest('div[class*="level-"]');
      const parentMenu = currentLevelMenu.previousElementSibling as HTMLDivElement;
      const parentLink = parentMenu.querySelector('a[aria-expanded="true"]') as HTMLAnchorElement;
      parentLink.click();
      parentLink.focus();
    }

  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);

    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  let Level2Component: React.FunctionComponent<Level2LayoutProps> | null = null
  if (level1active) {
    Level2Component = Level2Layouts[level1active.slug] !== undefined ? Level2Layouts[level1active.slug] : Level2Layouts['default']
  }

  let Level3Component: React.FunctionComponent<Level3LayoutProps> | null = null
  if (level2active) {
    Level3Component = Level3Layouts[level2active.slug] !== undefined ? Level3Layouts[level2active.slug] : Level3Layouts['default']
  }

  let Level2Items = level2.map(item => {
    return !hiddenLevel2.includes(item.slug) && <>
      <li key={item.id} onClick={(e) => { goto(e, item) }} className={item.active ? 'active' : ''}>
        <div className="item">
          <img className="icon" src={item.menusIcon.url} width={item.menusIcon.width} height={item.menusIcon.height} alt="" />
          <img className="icon-hover" src={item.menusIconHover.url} width={item.menusIconHover.width} height={item.menusIconHover.height} alt="" />
          <a href={item.url} className="secondary-link" aria-expanded={(item.active || item.slug === path[0]) ? "true" : "false"} aria-controls={item.slug} >{item.title}</a>
        </div>
      </li>
      {!isTablet && item.active && level2active && Level3Component && <Level3Component level2active={level2active} />}
    </>
  })

  return (
    <>
      <div ref={barsRef} className={'bars'}>
        <div className="level-1">
        {alert && alert.alerts.map(alert =>
            <div className={'alert alert-' + alert.severity}>
              {alert.icon && alert.icon !== 'none' && <img width="16" height="16" src={`/images/alert-icons/${alert.icon}-${alert.severity === 'yellow' ? 'black' : 'white'}.svg`} />}
              {alert.text}
              {alert.linkTarget && <a href={alert.linkTarget}>{alert.linkLabel ? alert.linkLabel : 'Learn More'}</a>}
            </div>
          )}
          
          <div className={"navigation"}>
            <div className="inner">
              <a className="logo" href="/" rel="home" title="Go Home">
                <span>Burnaby Public Library</span>
              </a>
              <nav className="nav-primary">
                <ul>
                  {primary.map((item) => <MenuItem key={item.id} item={item} />)}
                </ul>
              </nav>
              <nav className="nav-secondary">
                <ul>
                  {secondary.map((item) => <MenuItem key={item.id} item={item} />)}
                </ul>
              </nav>
            </div>
          </div>
        </div>
          {Level2Component !== null &&
            <Level2Component level1active={level1active} level2={Level2Items} handleClose={closeMenu} handleSearch={handleSearch} events={events} />
          }
          {(isTablet || (!isTablet && menuState === '/search/results')) && Level3Component !== null &&
            <Level3Component level2active={level2active} />
          }
      </div>
        {level1active &&
          <div
            className="modal-backdrop"
            onWheel={() => { closeMenu() }}
            onClick={() => { closeMenu() }}
          />
        }
    </>
  )
}